/**
 * Screen 0 — 承刃终端 首页（v7.0）
 *
 * 职责：
 *   - 注入 HTML 结构到 #app
 *   - 创建 Canvas 背景动画（title-canvas-background）
 *   - 管理入场动画序列（标题打字机 → 锁定 → 副标题+双入口淡入）
 *   - 交互彩蛋（雷达悬停）
 *
 * cleanup() 在路由离开时释放所有资源。
 */

import { createTitleBackground, type TitleBgController } from "../render/title-canvas-background";
import { t } from "../i18n";
import { isProductionDeploy, getAuthToken, setAuthSession, clearAuth, getUserProfile } from "../services/llm-service";
import { shouldShowOnboarding, getResumeRoute } from "../onboarding/onboarding-state";
import { renderEnlistment } from "../onboarding/onboarding-enlist";
import { playDemoReplay, renderDemoEnd } from "../onboarding/onboarding-demo";
import { setOnboardingStep } from "../onboarding/onboarding-state";

// ===== 动画时序常量 =====
/** 标题字溶解每字间隔 (ms) */
const CHAR_DISSOLVE_INTERVAL = 100;
/** 世界背景打字机每字延迟 (ms) */
const WORLD_TYPE_SPEED = 25;
/** 世界背景行间停顿 (ms) */
const WORLD_LINE_PAUSE = 220;
/** 三行诗打字机每字延迟 (ms) */
const POEM_TYPE_SPEED = 70;
/** 三行诗行间停顿 (ms) */
const POEM_LINE_PAUSE = 350;

// ===== 导出入口 =====

/**
 * 渲染右上角登录 UI（GitHub + Google + 账号密码）
 * 根据登录状态显示不同内容：
 *   - 未登录：OAuth 按钮 + "账号登录" 入口
 *   - 账号登录表单：用户名/密码输入 + 提交
 *   - 已登录：头像 + 用户名 + 注销
 */
let loginFormVisible = false;
const PENDING_AUTH_PATH_KEY = "battlecode-pending-auth-path";

function renderAuthUI(container: HTMLElement, onNavigate?: (path: string) => void): void {
  const token = getAuthToken();
  const profile = getUserProfile();

  if (token && profile) {
    const initial = profile.name ? profile.name.charAt(0).toUpperCase() : "?";
    const avatarHtml = profile.avatar
      ? `<img class="s0-auth-avatar-img" src="${escapeHtml(profile.avatar)}" alt="${escapeHtml(profile.name)}">`
      : `<span class="s0-auth-avatar-fallback">${initial}</span>`;

    container.innerHTML = `
      <div class="s0-auth-user">
        <div class="s0-auth-avatar">${avatarHtml}</div>
        <span class="s0-auth-name">${escapeHtml(profile.name)}</span>
        <button class="s0-auth-logout" title="退出登录">&#x2715;</button>
      </div>
    `;

    container.querySelector(".s0-auth-logout")?.addEventListener("click", async () => {
      loginFormVisible = false;
      const logoutButton = container.querySelector(".s0-auth-logout") as HTMLButtonElement | null;
      if (logoutButton) logoutButton.disabled = true;
      await clearAuth();
      renderAuthUI(container, onNavigate);
    });
  } else if (loginFormVisible) {
    // 显示账号密码登录表单
    container.innerHTML = `
      <form class="s0-auth-form">
        <div class="s0-auth-form-group">
          <input class="s0-auth-input" type="text" name="username" placeholder="用户名" autocomplete="username" required>
        </div>
        <div class="s0-auth-form-group">
          <input class="s0-auth-input" type="password" name="password" placeholder="密码" autocomplete="current-password" required>
        </div>
        <div class="s0-auth-form-actions">
          <button type="submit" class="s0-auth-form-btn">登录</button>
          <button type="button" class="s0-auth-form-back">&#x2190; 返回</button>
        </div>
        <p class="s0-auth-form-error" style="display:none"></p>
      </form>
    `;

    const errorEl = container.querySelector(".s0-auth-form-error") as HTMLElement;

    container.querySelector("form")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const username = (form.querySelector("[name=username]") as HTMLInputElement).value.trim();
      const password = (form.querySelector("[name=password]") as HTMLInputElement).value;

      if (!username || !password) {
        errorEl.textContent = "请输入用户名和密码";
        errorEl.style.display = "block";
        return;
      }

      errorEl.style.display = "none";
      const submitBtn = form.querySelector(".s0-auth-form-btn") as HTMLButtonElement;
      submitBtn.disabled = true;
      submitBtn.textContent = "登录中...";

      try {
        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });
        const data = await res.json();

        if (!data.ok) {
          errorEl.textContent = data.message || "登录失败";
          errorEl.style.display = "block";
          submitBtn.disabled = false;
          submitBtn.textContent = "登录";
          return;
        }

        // 原子建立账号会话：旧账号先同步，新账号再 hydrate。
        await setAuthSession(data.token, { openid: data.openid, name: data.name, provider: data.provider, avatar: data.avatar });
        loginFormVisible = false;
        renderAuthUI(container, onNavigate);
        const pendingPath = localStorage.getItem(PENDING_AUTH_PATH_KEY);
        if (pendingPath && onNavigate) {
          localStorage.removeItem(PENDING_AUTH_PATH_KEY);
          window.setTimeout(() => onNavigate(pendingPath), 250);
        }
      } catch {
        errorEl.textContent = "网络错误，请重试";
        errorEl.style.display = "block";
        submitBtn.disabled = false;
        submitBtn.textContent = "登录";
      }
    });

    container.querySelector(".s0-auth-form-back")?.addEventListener("click", () => {
      loginFormVisible = false;
      renderAuthUI(container, onNavigate);
    });
  } else {
    // 显示 OAuth 按钮 + 账号登录入口
    container.innerHTML = `
      <button class="s0-auth-btn s0-auth-btn--github" title="GitHub 登录" data-provider="github">
        <svg class="s0-auth-icon" viewBox="0 0 24 24" width="16" height="16">
          <path fill="currentColor" d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
        </svg>
        <span class="s0-auth-btn-text">GitHub</span>
      </button>
      <button class="s0-auth-btn s0-auth-btn--google" title="Google 登录" data-provider="google">
        <svg class="s0-auth-icon" viewBox="0 0 24 24" width="16" height="16">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span class="s0-auth-btn-text">Google</span>
      </button>
      <button class="s0-auth-link" title="使用账号密码登录">账号登录</button>
    `;

    // 注册 OAuth 按钮点击事件
    container.querySelectorAll(".s0-auth-btn").forEach(btn => {
      const provider = (btn as HTMLElement).dataset.provider;
      btn.addEventListener("click", () => {
        if (provider) {
          window.location.href = `/api/auth/${provider}/login`;
        }
      });
    });

    // 切换到账号登录表单
    container.querySelector(".s0-auth-link")?.addEventListener("click", () => {
      loginFormVisible = true;
      renderAuthUI(container, onNavigate);
    });
  }
}

export function renderScreen0Title(
  app: HTMLElement,
  onNavigate: (path: string) => void
): void {
  // ===== 注入 HTML =====
  app.innerHTML = `
    <div class="s0-root">
      <canvas id="title-bg-canvas" class="s0-canvas"></canvas>

      <!-- 赛博噪声码层（独立于主 canvas，CSS 动画驱动） -->
      <div class="s0-glitch-noise">
        <div class="s0-glitch-noise__char"></div>
        <div class="s0-glitch-noise__char"></div>
        <div class="s0-glitch-noise__char"></div>
        <div class="s0-glitch-noise__char"></div>
        <div class="s0-glitch-noise__char"></div>
        <div class="s0-glitch-noise__char"></div>
      </div>

      <!-- HUD 四角括号（粗线 1px + 末端 3px 短横） -->
      <div class="s0-hud-corners">
        <span class="s0-hud-corner s0-hud-corner--tl"></span>
        <span class="s0-hud-corner s0-hud-corner--tr"></span>
        <span class="s0-hud-corner s0-hud-corner--bl"></span>
        <span class="s0-hud-corner s0-hud-corner--br"></span>
      </div>

      <!-- 顶部状态条 -->
      <div class="s0-status-bar" id="s0-status-bar">
        <span class="s0-status-dot"></span>
        <span class="s0-status-label">ONLINE</span>
        <span class="s0-status-divider">//</span>
        <span class="s0-status-label s0-status-label--armed">SYS:ARMED</span>
        <span class="s0-status-divider">//</span>
        <span class="s0-status-label s0-status-label--detail">T-01 FORGE READY</span>
        <span class="s0-status-divider">//</span>
        <span class="s0-status-label s0-status-label--sync">SYNC 100%</span>
      </div>

      <!-- 左侧标语柱 -->
      <div class="s0-side-label">
        <span>F</span><span>O</span><span>R</span><span>G</span><span>E</span>
        <span class="s0-side-label__gap"></span>
        <span>O</span><span>R</span>
        <span class="s0-side-label__gap"></span>
        <span>B</span><span>E</span>
        <span class="s0-side-label__gap"></span>
        <span>F</span><span>O</span><span>R</span><span>G</span><span>E</span><span>D</span>
      </div>

      <!-- 边缘渐暗（vignette） -->
      <div class="s0-vignette"></div>
      <div class="s0-overlay"></div>

      <!-- Hero 插画（右半区域，半透明叠在 Canvas 背景之上） -->
      <img class="s0-hero-img" src="/images/ui/title-hero.png" alt="" aria-hidden="true">

      <!-- 对角线分割线 SVG（双线 + 15 刻度节点 + 端点标号） -->
      <svg class="s0-diagonal-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="s0-diag-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="var(--valorant-red, #FF4655)" />
            <stop offset="50%" stop-color="var(--tactical-cyan, #00E4FF)" />
            <stop offset="100%" stop-color="var(--tactical-cyan, #00E4FF)" />
          </linearGradient>
        </defs>
        <!-- 副对角线（平行偏移，营造刻度感） -->
        <line x1="0.28" y1="0.28" x2="99.72" y2="99.72" stroke="var(--tactical-cyan, #00E4FF)" stroke-width="0.4" vector-effect="non-scaling-stroke" opacity="0.25" class="s0-diag-sub" />
        <!-- 主对角线（45°，与背景网格斜率一致，左上→右下） -->
        <line x1="0" y1="0" x2="100" y2="100" stroke="url(#s0-diag-grad)" stroke-width="1.2" vector-effect="non-scaling-stroke" class="s0-diag-main" />
        <!-- 刻度节点（沿 0,0→100,100 均布，双端点 + 中点特大） -->
        <circle cx="5.0" cy="5.0" r="0.45" fill="var(--tactical-cyan, #00E4FF)" opacity="0.5" class="s0-diag-tick" />
        <circle cx="10.0" cy="10.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="15.0" cy="15.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="20.0" cy="20.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="25.0" cy="25.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="30.0" cy="30.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="35.0" cy="35.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="40.0" cy="40.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <!-- 中点大型节点 -->
        <circle cx="50.0" cy="50.0" r="0.7" fill="var(--tactical-cyan, #00E4FF)" opacity="0.8" class="s0-diag-tick s0-diag-tick--mid" />
        <circle cx="60.0" cy="60.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="65.0" cy="65.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="70.0" cy="70.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="75.0" cy="75.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="80.0" cy="80.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="85.0" cy="85.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
        <circle cx="90.0" cy="90.0" r="0.35" fill="var(--tactical-cyan, #00E4FF)" opacity="0.45" class="s0-diag-tick" />
      </svg>
      <!-- 对角线中点标签 -->
      <span class="s0-diag-label">FORGE → QUENCH</span>
      <!-- 对角线端点标号 -->
      <span class="s0-diag-ep s0-diag-ep--tl">01</span>
      <span class="s0-diag-ep s0-diag-ep--br">04</span>

      <!-- 左面板：标题 + 三行诗/世界观舞台 + 入口 -->
      <div class="s0-panel-left">
        <h1 class="s0-left-title" id="s0-left-title"></h1>
        <p class="s0-left-subtitle" id="s0-left-subtitle"></p>
        <div class="s0-stage" id="s0-stage">
          <div class="s0-stage-world" id="s0-stage-world">
            <p class="s0-world-line" data-wb="1">${t("home.world_bg_line1")}</p>
            <p class="s0-world-line" data-wb="2">${t("home.world_bg_line2")}</p>
            <p class="s0-world-line" data-wb="3">${t("home.world_bg_line3")}</p>
            <p class="s0-world-line" data-wb="4">${t("home.world_bg_line4")}</p>
            <p class="s0-world-line" data-wb="5">${t("home.world_bg_line5")}</p>
          </div>
          <div class="s0-stage-poem" id="s0-stage-poem">
            <p class="s0-poem-line" data-poem="1">${t("home.poem_line1")}</p>
            <p class="s0-poem-line" data-poem="2">${t("home.poem_line2")}</p>
            <p class="s0-poem-line" data-poem="3">${t("home.poem_line3")}</p>
          </div>
        </div>
      </div>

      <!-- 入口按钮组（脱离 panel-left 的 clip-path，独立 fixed 定位，避免被梯形裁切） -->
      <div class="s0-left-entries" id="s0-left-entries">
        <button class="s0-entry-btn" id="s0-btn-forge">
          <span class="s0-entry-label">${t("home.entry_forge_label")}</span>
          <span class="s0-entry-sub">${t("home.entry_forge_sub")}</span>
        </button>
        <button class="s0-entry-btn" id="s0-btn-sharpen">
          <span class="s0-entry-label">${t("home.entry_sharpen_label")}</span>
          <span class="s0-entry-sub">${t("home.entry_sharpen_sub")}</span>
        </button>
        <button class="s0-entry-btn" id="s0-btn-bear">
          <span class="s0-entry-label">${t("home.entry_bear_label")}</span>
          <span class="s0-entry-sub">${t("home.entry_bear_sub")}</span>
        </button>
        <button class="s0-entry-btn" id="s0-btn-sandbox">
          <span class="s0-entry-label">${t("home.entry_sandbox_label")}</span>
          <span class="s0-entry-sub">${t("home.entry_sandbox_sub")}</span>
        </button>
        <button class="s0-entry-btn" id="s0-btn-armory">
          <span class="s0-entry-label">${t("home.entry_armory_label")}</span>
          <span class="s0-entry-sub">${t("home.entry_armory_sub")}</span>
        </button>

      </div>

      <!-- 导航链接 -->
      <nav class="s0-nav" id="s0-nav">
        <a href="#/levels" class="s0-nav-link" id="s0-link-levels">${t("home.nav_levels")}</a>
        <a href="#/settings" class="s0-nav-link" id="s0-link-settings">${t("home.nav_settings")}</a>
      </nav>

      <footer class="s0-footer">${t("home.footer")}</footer>

      <!-- V11 登录入口：GitHub + Google（右上角，仅生产环境） -->
      <div class="s0-auth-area" id="s0-auth-area" ${isProductionDeploy() ? "" : 'hidden'}></div>
    </div>
  `;

  // ===== 注册按钮点击 =====
  app.querySelector("#s0-btn-forge")?.addEventListener("click", () => {
    onNavigate("#/forge");
  });
  app.querySelector("#s0-btn-sharpen")?.addEventListener("click", () => {
    // V8 改造:砺锋台入口改为先经过中转页(剑从鞘中抽出动画,不可跳过)
    onNavigate("#/sharpen-transition");
  });
  app.querySelector("#s0-btn-bear")?.addEventListener("click", () => {
    onNavigate("#/levels");
  });
  app.querySelector("#s0-btn-sandbox")?.addEventListener("click", () => {
    onNavigate("#/my-works");
  });
  app.querySelector("#s0-btn-armory")?.addEventListener("click", () => {
    onNavigate("#/armory-hall");
  });

  // ===== V16 新手引导：新用户检测 =====
  if (shouldShowOnboarding()) {
    // 刷新恢复：检测是否在引导中期刷新页面
    const resumeRoute = getResumeRoute();
    if (resumeRoute) {
      // 引导进行中且已过早期步骤 → 直接续接到对应屏幕
      onNavigate(resumeRoute);
      // 提前返回，不渲染首页 UI（避免闪烁）
      return;
    }

    // 早期步骤或首次进入 → 显示简化首页 + 「开始」
    // 隐藏入口按钮组
    const leftEntries = app.querySelector("#s0-left-entries") as HTMLElement | null;
    if (leftEntries) leftEntries.style.display = "none";

    // 添加「开始」按钮
    const startBtnContainer = document.createElement("div");
    startBtnContainer.className = "s0-onboarding-start-wrap";
    startBtnContainer.innerHTML = `
      <button class="s0-onboarding-start-btn pulse-highlight">开 始</button>
    `;
    // 插入到 left-entries 的位置之后
    if (leftEntries?.parentElement) {
      leftEntries.parentElement.appendChild(startBtnContainer);
    } else {
      app.appendChild(startBtnContainer);
    }

    const startBtn = startBtnContainer.querySelector(".s0-onboarding-start-btn") as HTMLButtonElement;
    startBtn.addEventListener("click", async () => {
      startBtn.disabled = true;

      // 1. 录取通知书
      const name = await renderEnlistment(app);
      if (!name) {
        // 用户点返回，恢复按钮
        startBtn.disabled = false;
        return;
      }

      // 2. 3v3 演示回放
      await playDemoReplay(app);

      // 3. 演示结束页
      await renderDemoEnd(app);

      // 4. 进入铸剑阁
      setOnboardingStep("forge-ch0");
      onNavigate("#/forge");
    });
  }

  // ===== V11 GitHub + Google OAuth 登录 =====
  const authArea = app.querySelector("#s0-auth-area") as HTMLElement | null;
  if (authArea) {
    renderAuthUI(authArea, onNavigate);
  }

  // ===== Canvas 背景 =====
  const canvas = app.querySelector("#title-bg-canvas") as HTMLCanvasElement | null;
  let bgCtrl: TitleBgController | null = null;

  if (canvas) {
    bgCtrl = createTitleBackground(canvas);
    // 必须先 start() 初始化背景状态，再 resize()
    bgCtrl.start();
    bgCtrl.resize();

    // Resize 监听
    const ro = new ResizeObserver(() => {
      bgCtrl?.resize();
    });
    ro.observe(canvas.parentElement ?? canvas);
  }

  // ===== 动画序列 =====

  const timers: number[] = [];

  // ---- 工具函数 ----
  const sleep = (ms: number) => new Promise<void>(resolve => {
    const t = window.setTimeout(resolve, ms);
    timers.push(t);
  });

  // ---- 标题字溶解式出现 ----
  const titleEl = app.querySelector("#s0-left-title") as HTMLElement | null;
  const titleText = t("home.title_line1");

  if (titleEl) {
    // 把每个字符（含空格）包装成 <span class="s0-char" style="animation-delay:...">
    const chars = Array.from(titleText);
    titleEl.innerHTML = chars.map((ch, i) =>
      `<span class="s0-char" style="animation-delay:${i * CHAR_DISSOLVE_INTERVAL}ms">${escapeHtml(ch)}</span>`
    ).join("");
    titleEl.classList.add("s0-left-title--visible");
  }

  // ---- 副标题淡入 ----
  const subtitleEl = app.querySelector("#s0-left-subtitle") as HTMLElement | null;
  if (subtitleEl) {
    subtitleEl.textContent = t("home.title_line2");
  }

  // ---- 主动画序列（异步，开机仪式 4.5s） ----
  async function runAnimation(): Promise<void> {
    const rootEl = app.querySelector(".s0-root") as HTMLElement | null;
    if (!rootEl) return;

    // Phase 1: Power On (0-500ms)
    // T+0ms   Status bar 滑入
    const statusBar = app.querySelector("#s0-status-bar") as HTMLElement | null;
    statusBar?.classList.add("s0-status-bar--visible");
    await sleep(300);

    // T+300ms 对角线描边动画
    const diagSvg = app.querySelector(".s0-diagonal-svg") as SVGElement | null;
    diagSvg?.classList.add("s0-diagonal-svg--visible");

    // T+300ms Hero 插画淡入（右半区，半透明代码人像）
    const heroImg = app.querySelector(".s0-hero-img") as HTMLElement | null;
    heroImg?.classList.add("s0-hero-img--visible");
    await sleep(200);

    // T+500ms HUD 四角淡入
    const hudCorners = rootEl.querySelector(".s0-hud-corners") as HTMLElement | null;
    hudCorners?.classList.add("s0-hud-corners--visible");

    // Phase 2: Title Reveal (500-1400ms)
    // 等待标题字溶解完成（CSS animation，由 s0-left-title--visible 触发）
    const titleCharCount = Array.from(titleText).length;
    await sleep(titleCharCount * CHAR_DISSOLVE_INTERVAL - 500 + 300);

    // T+~1100ms 副标题淡入
    subtitleEl?.classList.add("s0-left-subtitle--visible");
    await sleep(400);

    // Phase 3: Content Loop (标题完成后开始，无限循环：world → poem → both)
    const worldLines = Array.from(app.querySelectorAll(".s0-world-line")) as HTMLElement[];
    const poemLines = Array.from(app.querySelectorAll(".s0-poem-line")) as HTMLElement[];
    const stageWorld = app.querySelector("#s0-stage-world") as HTMLElement | null;
    const stagePoem = app.querySelector("#s0-stage-poem") as HTMLElement | null;

    const worldTexts = worldLines.map(el => el.textContent || "");
    const poemTexts = poemLines.map(el => el.textContent || "");

    function clearWorld() {
      worldLines.forEach(el => {
        el.textContent = "";
        el.style.visibility = "hidden";
        el.classList.remove("s0-world-line--typing");
      });
    }

    function clearPoem() {
      poemLines.forEach(el => {
        el.textContent = "";
        el.style.visibility = "hidden";
        el.classList.remove("s0-poem-line--typing", "s0-poem-line--complete");
      });
    }

    async function typeWorld() {
      for (let i = 0; i < worldLines.length; i++) {
        const el = worldLines[i];
        const text = worldTexts[i];
        el.style.visibility = "visible";
        el.classList.add("s0-world-line--typing");

        for (let j = 0; j < text.length; j++) {
          el.textContent = text.substring(0, j + 1);
          await sleep(WORLD_TYPE_SPEED);
        }
        el.classList.remove("s0-world-line--typing");
        if (i < worldLines.length - 1) {
          await sleep(WORLD_LINE_PAUSE);
        }
      }
    }

    async function typePoem() {
      for (let i = 0; i < poemLines.length; i++) {
        const el = poemLines[i];
        const text = poemTexts[i];
        el.style.visibility = "visible";
        el.classList.add("s0-poem-line--typing");

        for (let j = 0; j < text.length; j++) {
          el.textContent = text.substring(0, j + 1);
          await sleep(POEM_TYPE_SPEED);
        }
        el.classList.remove("s0-poem-line--typing");
        el.classList.add("s0-poem-line--complete");

        if (i < poemLines.length - 1) {
          await sleep(POEM_LINE_PAUSE);
        }
      }
    }

    async function contentLoop() {
      while (true) {
        // 1. 重置并隐藏两层
        stageWorld?.classList.remove("s0-stage-world--visible");
        stagePoem?.classList.remove("s0-stage-poem--visible");
        await sleep(600);
        clearWorld();
        clearPoem();

        // 2. 世界观
        clearWorld();
        stageWorld?.classList.add("s0-stage-world--visible");
        await sleep(600);
        await typeWorld();
        await sleep(1200);

        // 3. 三行诗
        stageWorld?.classList.remove("s0-stage-world--visible");
        clearPoem();
        stagePoem?.classList.add("s0-stage-poem--visible");
        await sleep(600);
        await typePoem();
        await sleep(1200);

        // 4. 世界观
        stagePoem?.classList.remove("s0-stage-poem--visible");
        clearWorld();
        stageWorld?.classList.add("s0-stage-world--visible");
        await sleep(600);
        await typeWorld();
        await sleep(1200);

        // 5. 三行诗
        stageWorld?.classList.remove("s0-stage-world--visible");
        clearPoem();
        stagePoem?.classList.add("s0-stage-poem--visible");
        await sleep(600);
        await typePoem();
        await sleep(1200);
      }
    }

    // 后台启动内容循环，不阻塞 HUD 入场
    contentLoop();

    // Phase 4: HUD 入场 (标题完成后同时开始)
    // 按钮 stagger 入场（150ms 间隔）
    const entryBtns = app.querySelectorAll(".s0-entry-btn");
    for (let i = 0; i < entryBtns.length; i++) {
      entryBtns[i].classList.add("s0-entry-btn--visible");
      await sleep(150);
    }

    // 导航链接淡入
    await sleep(100);
    const navLinks = app.querySelectorAll(".s0-nav-link");
    navLinks.forEach(el => el.classList.add("s0-nav-link--visible"));

    await sleep(300);

    // Phase 5: Final Polish (4200ms+)
    // HUD 装饰 + 侧标签
    const sideLabel = rootEl.querySelector(".s0-side-label") as HTMLElement | null;
    sideLabel?.classList.add("s0-side-label--visible");

    // 对角线标签 + 端点标号
    const diagLabel = rootEl.querySelector(".s0-diag-label") as HTMLElement | null;
    const diagEps = rootEl.querySelectorAll(".s0-diag-ep");
    diagLabel?.classList.add("s0-diag-label--visible");
    diagEps.forEach(el => el.classList.add("s0-diag-ep--visible"));

    // 噪声码启动
    startGlitchNoise(app);
  }

  // 200ms 后启动动画序列
  timers.push(window.setTimeout(() => {
    runAnimation();
  }, 200));

  // ===== 赛博噪声码 =====
  const GLITCH_HEX_CHARS = "0123456789ABCDEF";
  let glitchNoiseTimer = 0;
  let glitchNoiseEls: HTMLElement[] = [];

  function randomHexPair(): string {
    return GLITCH_HEX_CHARS[Math.floor(Math.random() * 16)] +
           GLITCH_HEX_CHARS[Math.floor(Math.random() * 16)];
  }

  function startGlitchNoise(container: HTMLElement): void {
    glitchNoiseEls = Array.from(
      container.querySelectorAll(".s0-glitch-noise__char")
    ) as HTMLElement[];

    if (glitchNoiseEls.length === 0) return;

    glitchNoiseTimer = window.setInterval(() => {
      // 随机选 1-3 个噪声位更新内容
      const count = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        const idx = Math.floor(Math.random() * glitchNoiseEls.length);
        glitchNoiseEls[idx].textContent = randomHexPair();
      }
    }, 400);
  }

  // ===== cleanup — 路由离开时调用 =====
  app._s0Cleanup = () => {
    bgCtrl?.stop();
    canvas?.remove();
    timers.forEach(clearTimeout);
    timers.length = 0;
    clearInterval(glitchNoiseTimer);
  };
}

/** HTML 特殊字符转义 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return text.replace(/[&<>"']/g, ch => map[ch] || ch);
}

// 扩展 HTMLElement 类型以支持 cleanup
declare global {
  interface HTMLElement {
    _s0Cleanup?: () => void;
  }
}

// 在 navigate 时调用 cleanup
// 注意：main.ts 的 navigate 在 renderScreenXxx 之前会调用前一个屏的 cleanup
// 这里通过 _s0Cleanup 模式实现
export function cleanupTitleScreen(app: HTMLElement): void {
  app._s0Cleanup?.();
}
